import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { teacherApi } from '../../api/teacherApi'

// Async thunks
export const fetchTeacherProfile = createAsyncThunk(
  'teacher/fetchProfile',
  async (_, { rejectWithValue }) => {
    try {
      const response = await teacherApi.getTeacherProfile()
      return response
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to fetch profile'
      )
    }
  }
)

export const updateTeacherProfile = createAsyncThunk(
  'teacher/updateProfile',
  async (profileData, { rejectWithValue }) => {
    try {
      const response = await teacherApi.updateTeacherProfile(profileData)
      return response
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to update profile'
      )
    }
  }
)

export const fetchStaffDashboard = createAsyncThunk(
  'teacher/fetchDashboard',
  async (_, { rejectWithValue }) => {
    try {
      const response = await teacherApi.getStaffDashboard()
      return response
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to fetch dashboard data'
      )
    }
  }
)

export const fetchStaffStatistics = createAsyncThunk(
  'teacher/fetchStatistics',
  async (_, { rejectWithValue }) => {
    try {
      const response = await teacherApi.getStaffStatistics()
      return response
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to fetch statistics'
      )
    }
  }
)

const initialState = {
  profile: null,
  dashboard: null,
  statistics: null,
  isLoading: false,
  error: null,
  lastUpdated: null,
}

const teacherSlice = createSlice({
  name: 'teacher',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null
    },
    resetTeacherState: (state) => {
      state.profile = null
      state.dashboard = null
      state.statistics = null
      state.error = null
      state.lastUpdated = null
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Profile
      .addCase(fetchTeacherProfile.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(fetchTeacherProfile.fulfilled, (state, action) => {
        state.isLoading = false
        state.profile = action.payload
        state.lastUpdated = Date.now()
      })
      .addCase(fetchTeacherProfile.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload
      })

      // Update Profile
      .addCase(updateTeacherProfile.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(updateTeacherProfile.fulfilled, (state, action) => {
        state.isLoading = false
        state.profile = action.payload
        state.lastUpdated = Date.now()
      })
      .addCase(updateTeacherProfile.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload
      })

      // Fetch Dashboard
      .addCase(fetchStaffDashboard.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(fetchStaffDashboard.fulfilled, (state, action) => {
        state.isLoading = false
        state.dashboard = action.payload
        state.lastUpdated = Date.now()
      })
      .addCase(fetchStaffDashboard.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload
      })

      // Fetch Statistics
      .addCase(fetchStaffStatistics.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(fetchStaffStatistics.fulfilled, (state, action) => {
        state.isLoading = false
        state.statistics = action.payload
        state.lastUpdated = Date.now()
      })
      .addCase(fetchStaffStatistics.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload
      })
  },
})

export const { clearError, resetTeacherState } = teacherSlice.actions
export default teacherSlice.reducer